<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >

    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
        md="5"
      >
        <BFormGroup
          label="Province"
          label-for="filterProvince"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableCustomers.selected.province"
            placeholder="filter province here"
            input-id="filterProvince"
            label="province_name"
            class="border border-light rounded-lg"
            clearable
            append-to-body
            :options="(tableCustomers.options.provinces)"
            :loading="(tableCustomers.fetching.provinces)"
            :disabled="(tableCustomers.busy || tableCustomers.fetching.provinces)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterProvinces"
          >
            <template #option="{ province_name, region }">
              <div
                class="text-break text-wrap"
                v-text="province_name"
              />
              <div
                class="text-muted small"
                v-text="region.region_name"
              />
            </template>
            <template #selected-option="{ province_name }">
              <div
                class="w-225px w-md-175px w-lg-325px text-truncate"
                v-text="province_name"
              />
            </template>
            <template #no-options="">
              no available province
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="5"
      >
        <BFormGroup
          label="Municipality"
          label-for="filterMunicipality"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableCustomers.selected.municipality"
            placeholder="filter municipality here"
            input-id="filterMunicipality"
            label="municipality_name"
            class="border border-light rounded-lg"
            clearable
            append-to-body
            :options="(tableCustomers.options.municipalities)"
            :loading="(tableCustomers.fetching.municipalities)"
            :disabled="(tableCustomers.busy || tableCustomers.fetching.municipalities || tableCustomers.fetching.provinces)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterMunicipalities"
          >
            <template #option="{ municipality_name }">
              <div
                class="text-break text-wrap"
                v-text="municipality_name"
              />
            </template>
            <template #selected-option="{ municipality_name }">
              <div
                class="w-225px w-md-175px w-lg-325px text-truncate"
                v-text="municipality_name"
              />
            </template>
            <template #no-options="">
              no available municipality
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Status"
          label-for="filterStatus"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormSelect
            id="filterStatus"
            v-model="tableCustomers.filter.status"
            :options="tableCustomers.options.statuses"
          >
            <template #first>
              <BFormSelectOption
                value=""
                disabled
                v-text="' - - filter here - - '"
              />
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableCustomers.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createCustomer"
        >
          Add Customer
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableCustomers"
      class="mt-3"
      :provider="tableProvider"
      :fields="tableCustomers.fields"
      :filter="tableCustomers.filter"
      :filter-on="tableCustomers.filterOn"
      :sort-by="tableCustomers.sortBy"
      :sort-desc="tableCustomers.sortDesc"
      :sort-direction="tableCustomers.sortDirection"
      :total-rows="tableCustomers.totalRows"
      :current-page="tableCustomers.currentPage"
      :per-page="tableCustomers.perPage"
      :stacked="tableCustomers.stacked"
      :busy="tableCustomers.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableCustomers.busy"
            :to="{
              name: 'admin.customers.profile',
              params: {
                customer: item.id
              }
            }"
          >
            Manage
          </BButton>
        </div>
      </template>

      <template #cell(status)="{ value }">
        <BBadge
          class="w-60px"
          :variant="`${customerStatus(value)}-light`"
          pill
          v-text="value"
        />
      </template>
    </CustomTable>

    <BModal
      ref="modalCustomer"
      size="xl"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <div class="px-2">
        <CustomerInformations
          ref="customerInformations"
          :editing="state.editing"
        />
      </div>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy || isFetchingInformationLocations()"
          @click.prevent="validateCustomer"
        >
          {{ state.editing ? 'Update' : 'Register' }} Customer
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import { mapActions, mapGetters } from 'vuex'

import CustomerInformations from '@/views/admin/customer-management/Informations.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'

import { SAdminCustomer } from '@/services/admin'

export default {

  name: 'VAdminCustomers',

  components: {
    CustomerInformations
  },

  mixins: [
    MProvinces,
    MMunicipalities,
    MxMisc,
    MxFormatter,
    MxImageUrl
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Customers'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      tableCustomers: {
        busy: false,
        fetching: {
          provinces: false,
          municipalities: false
        },
        options: {
          statuses: ['All', 'New', 'Verified'],
          provinces: [],
          municipalities: []
        },
        editable: {
          province: '',
          municipality: '',
          status: ''
        },
        selected: {
          tagging: 1,
          province: '',
          municipality: ''
        },
        filter: {
          search: '',
          status: this.customerTStatus,
          province: this.customerTProvince,
          municipality: this.customerTMunicipality
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 8, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 4, key: 'created_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 1, key: 'last_name', label: 'Name', class: 'min-w-md-250px', formatter: this.getFullName, sortable: true },
          { mobile: 2, key: 'province.province_name', label: 'Province', class: 'min-w-250px' },
          { mobile: 3, key: 'municipality.municipality_name', label: 'Municipality', class: 'min-w-250px' },
          { mobile: 7, key: 'status', label: 'Status', class: 'min-w-80px text-right text-md-center' },
          { mobile: 5, key: 'updated_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({
    currentUser: 'auth/user',
    customerTProvince: 'customer-management/customerProvinceState',
    customerTMunicipality: 'customer-management/customerMunicipalityState',
    customerTStatus: 'customer-management/customerStatusState'
  }),

  watch: {
    'tableCustomers.selected.province' (province) {
      this.tableCustomers.filter.province = province?.id || ''

      if (province?.id) {
        this.getMunicipalities(this.tableCustomers, '', province.id).finally(() => {
          if (this.tableCustomers.editable.municipality) {
            this.tableCustomers.selected.municipality = QUERY.find(this.tableCustomers.options.municipalities, {
              id: this.tableCustomers.editable.municipality
            })

            this.tableCustomers.editable.municipality = ''
          }
        })
      } else {
        this.tableCustomers.selected.municipality = ''
        this.tableCustomers.options.municipalities = []
      }

      this.setTableProvince(province?.id || '')
    },

    'tableCustomers.selected.municipality' (municipality) {
      this.tableCustomers.filter.municipality = municipality?.id || ''

      this.setTableMunicipality(municipality?.id || '')
    },

    'tableCustomers.editable.status' (status) {
      if (status) {
        this.tableCustomers.filter.status = status
      }

      this.tableCustomers.editable.status = ''
    },

    'tableCustomers.filter.status' (status) {
      this.setTableStatus(status)
    }
  },

  mounted () {
    if (this.currentUser) {
      this.tableCustomers.editable.status = this.customerTStatus
      this.tableCustomers.editable.province = this.customerTProvince
      this.tableCustomers.editable.municipality = this.customerTMunicipality

      this.getProvinces(this.tableCustomers).finally(() => {
        if (this.tableCustomers.editable.province) {
          this.tableCustomers.selected.province = QUERY.find(this.tableCustomers.options.provinces, {
            id: this.tableCustomers.editable.province
          })

          this.tableCustomers.editable.province = ''
        }
      })
    }
  },

  methods: {

    ...mapActions({
      setTableProvince: 'customer-management/customerProvinceAction',
      setTableMunicipality: 'customer-management/customerMunicipalityAction',
      setTableStatus: 'customer-management/customerStatusAction'
    }),

    isFetchingInformationLocations () {
      if (this.$refs.customerInformations?.fetching) {
        const fetching = this.$refs.customerInformations.fetching
        return fetching?.provinces || fetching?.municipalities || fetching?.barangays
      }

      return false
    },

    customerStatus (status) {
      switch (status) {
        case 'Verified': {
          return 'secondary'
        }
        case 'New': {
          return 'primary'
        }
        default: {
          return 'warning'
        }
      }
    },

    async searchFilterProvinces (search, loading) {
      this.searchProvincesProvider(this.tableCustomers, search, loading)
    },

    async searchFilterMunicipalities (search, loading) {
      this.searchMunicipalitiesProvider(this.tableCustomers, search, loading)
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableCustomers.busy = true

      if (this.tableCustomers.editable.province || this.tableCustomers.editable.municipality || this.tableCustomers.editable.status) {
        return []
      }

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_status: filter.status,
        province_id: filter.province,
        municipality_id: filter.municipality
      }

      return await SAdminCustomer.get(payload).then(
        ({ data }) => {
          this.tableCustomers.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableCustomers.totalRows = 0
        return []
      }).finally(() => {
        this.tableCustomers.busy = false
      })
    },

    createCustomer () {
      this.state.editing = false

      this.$refs.modalCustomer.show()
      this.$refs.modalCustomer.onAfterEnter = () => {
        this.$refs.customerInformations.createInformation()
      }
    },

    validateCustomer () {
      this.$refs.customerInformations.submitInformation().then(
        ({ value }) => {
          if (value?.customer) {
            this.swalConfirm({
              html: 'Navigate to Customer Profile to Add More Information?',
              confirmButtonText: 'Navigate',
              cancelButtonText: 'Stay on Page',
              preConfirm: () => {
                this.$router.push({
                  name: 'fso.customers.profile',
                  params: {
                    customer: value.customer.id
                  }
                })
              }
            }).then(
              ({ isDismissed }) => {
                if (isDismissed) {
                  this.$refs.modalCustomer.hide()
                  this.$refs.tableCustomers.refetch()
                }
              }
            )
          }
        }
      )
    }
  }
}
</script>
